// Shared design tokens and style factories - the foundation layer of the full visual redesign.
//
// WHY THIS EXISTS
// The UX audit's central finding was that the app has no *integration* layer: each shell was
// built by copy-pasting the previous shell's style objects, so "card", "navy", "secondary
// button" and "warning banner" each quietly forked into 3-5 near-identical implementations.
// This module is the single place those concepts are defined, so they can converge instead of
// drifting further apart.
//
// RELATIONSHIP TO colors.js
// colors.js owns COLOR VALUES and nothing else. This module owns everything built FROM them -
// scales, and the composed style objects. It imports colors.js and never redefines a hex.
// There is deliberately no second color system.
//
// HOW THE VALUES WERE CHOSEN
// Not invented. Each scale step is the existing majority value for its role, counted across all
// ~1,100 inline style objects in the app. 85% of font sizes were already 11/12/13/14px and 79%
// of radii were already 6/8/10px, so this codifies the convention the codebase had already
// converged on by hand, and retires only the outliers (9/17/22/26/30px etc). A screen adopting
// these tokens should look like a tidier version of itself, not a different screen.
//
// STYLING CONVENTION
// The app styles exclusively via inline style={{}} objects - no CSS-in-JS, no stylesheets, no
// media queries. That constraint is respected here: these are plain objects, and interactive
// states (hover/focus) use the onFocus/onBlur/onMouseEnter handler pattern the codebase already
// uses, since inline styles cannot express real :focus/:hover pseudo-classes.

import {
  GOLD, NAVY_FORM, DANGER, SUCCESS, WARNING_BG, WARNING_BORDER, WARNING_TEXT,
  SURFACE, SURFACE_DARK, PAGE_BG, PAGE_BG_DARK, BORDER, BORDER_DARK, BORDER_INPUT,
  BORDER_INPUT_DARK, TEXT_PRIMARY, TEXT_MUTED, TEXT_FAINT, TEXT_DARK_MODE, FOCUS_RING,
} from './colors';
// REDESIGN Stage 1 tokens - imported here at the top (import/first) but consumed by the
// Stage 1 block at the end of this file.
import {
  NAVY_700, NAVY_600, GOLD_500,
  N_0, N_50, N_100, N_200, N_300, N_400, N_500, N_600, N_900,
  OK_600, OK_50, WARN_600, WARN_50, CRIT_600, CRIT_500, CRIT_50,
} from './colors';

// ── Typography ──────────────────────────────────────────────────────────────
// Six steps. Anything outside this scale is drift and should map to its nearest step.
export const FS = {
  micro:   '11px',  // uppercase labels, table headers
  small:   '12px',  // secondary text, badges, captions
  body:    '13px',  // DEFAULT - table cells, form values, nav items
  strong:  '14px',  // card titles, section headings
  h2:      '18px',  // page/screen titles
  display: '28px',  // KPI metric values only
};

export const FW = { normal: 400, medium: 500, semibold: 600, bold: 700 };

/** Uppercase micro-label treatment - table headers, metric labels, sidebar group headings.
 *  Already the app's existing convention; named here so it stops being retyped. */
export const LABEL_CAPS = {
  fontSize: FS.micro,
  fontWeight: FW.semibold,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

// ── Spacing ─────────────────────────────────────────────────────────────────
// 4px base. Card padding 20, card gap 16, field gap 12 - Dashboard.js's existing rhythm.
export const SP = { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '20px', xxl: '24px', xxxl: '32px' };

// ── Radii ───────────────────────────────────────────────────────────────────
export const R = {
  sm:   '4px',   // pills, small badges
  md:   '6px',   // DEFAULT - buttons, inputs
  lg:   '8px',   // nested/inner cards
  xl:   '10px',  // top-level cards
  xxl:  '12px',  // modals
  full: '999px', // circular
};

// ── Elevation ───────────────────────────────────────────────────────────────
// Dark mode uses borders rather than shadows (shadows are invisible on dark surfaces) - this is
// the existing convention in Dashboard.js and EmployeeProfile.js, preserved here.
export const SHADOW = {
  card:  '0 1px 3px rgba(0,0,0,0.05)',
  modal: '0 8px 32px rgba(0,0,0,0.2)',
  none:  'none',
};

/** Focus ring, applied via onFocus/onBlur. Inline styles can't express :focus, so every input
 *  that sets `outline: 'none'` needs this applied by handler to stay keyboard-navigable. */
export const focusRing = {
  boxShadow: `0 0 0 2px ${FOCUS_RING}`,
  borderColor: GOLD,
};

// ── Shared style factories ──────────────────────────────────────────────────
// mkT(dark) mirrors the mkS(dark) factory pattern already proven in Dashboard.js, EmployeeView.js
// and AdminPanel.js. Files adopt it incrementally by spreading its objects into their own local
// `s`, so a screen can converge one style at a time without a risky big-bang rewrite.

/** RESPONSIVE Phase 3: the ONE canonical sidebar-chrome shape. Previously the sidebar box was
 *  defined twice with slightly different values (mkT().sidebar: 200px / 0.5px border, and
 *  shellNav().sidebar: 240px / 1px border / minHeight / paddingTop). This de-dupes the STRUCTURE
 *  into one place; each caller still passes its own width/border/surface so no consumer's rendered
 *  result changes. `extra` carries any caller-specific props (e.g. shellNav's minHeight/paddingTop).
 *  flexShrink:0 is baked in (every sidebar had it) - the shared <Sidebar> also re-applies it. */
export const sidebarChrome = ({ width, surface, border, extra }) => ({
  width, flexShrink: 0, backgroundColor: surface, borderRight: border, ...(extra || {}),
});

export const mkT = (dark) => ({
  // Surfaces
  page: { backgroundColor: dark ? PAGE_BG_DARK : PAGE_BG, minHeight: '100vh' },

  /** THE card. Replaces the 4 competing neutral-container backgrounds in ComplianceModule
   *  (#fff/#f8f9fa/#fafafa + two border greys) and merges Dashboard's KpiCard/metricCard. */
  card: {
    backgroundColor: dark ? SURFACE_DARK : SURFACE,
    borderRadius: R.xl,
    padding: SP.xl,
    marginBottom: SP.lg,
    boxShadow: dark ? SHADOW.none : SHADOW.card,
    border: `0.5px solid ${dark ? BORDER_DARK : BORDER}`,
  },
  cardTitle: {
    fontSize: FS.strong,
    fontWeight: FW.semibold,
    color: dark ? TEXT_DARK_MODE : NAVY_FORM,
    margin: `0 0 ${SP.lg} 0`,
    display: 'flex',
    alignItems: 'center',
    gap: SP.sm,
  },

  // Text
  text:    { fontSize: FS.body, color: dark ? TEXT_DARK_MODE : TEXT_PRIMARY },
  muted:   { fontSize: FS.small, color: dark ? TEXT_MUTED : TEXT_FAINT },
  heading: { fontSize: FS.h2, fontWeight: FW.semibold, color: dark ? TEXT_DARK_MODE : NAVY_FORM, margin: 0 },
  label:   { ...LABEL_CAPS, color: dark ? TEXT_MUTED : TEXT_FAINT },
  /** Empty state - "you genuinely have no records". Distinct from loadError below; conflating
   *  the two is the audit's finding 6.2 and the reason a failed GET read as "you have nothing". */
  // A11YCONTRAST (audit A3b): the light-mode colour was #bbb, measured 1.92:1 on white, the
  // worst text contrast in the app. Now N_500, an existing ramp token, measured 4.97:1.
  empty:   { fontSize: FS.body, color: dark ? TEXT_MUTED : N_500, fontStyle: 'italic', margin: `${SP.sm} 0 0 0` },

  // Buttons - exactly four roles.
  btnPrimary: {
    backgroundColor: dark ? GOLD : NAVY_FORM,
    color: dark ? NAVY_FORM : '#fff',
    border: 'none', borderRadius: R.md, padding: '8px 16px',
    fontSize: FS.body, fontWeight: FW.medium, cursor: 'pointer',
  },
  /** Secondary for LIGHT surfaces. Using btnOnDark here instead is the audit's concrete
   *  EmployeeView Cancel-button bug (1.4) - white-on-transparent over a white card. */
  btnSecondary: {
    backgroundColor: 'transparent',
    color: dark ? TEXT_DARK_MODE : NAVY_FORM,
    border: `1px solid ${dark ? BORDER_INPUT_DARK : NAVY_FORM}`,
    borderRadius: R.md, padding: '8px 16px',
    fontSize: FS.body, fontWeight: FW.medium, cursor: 'pointer',
  },
  /** Secondary for DARK surfaces only (navy topbars). Never place on a light card. */
  btnOnDark: {
    backgroundColor: 'transparent', color: '#fff',
    border: '1px solid rgba(255,255,255,0.35)',
    borderRadius: R.md, padding: '8px 16px',
    fontSize: FS.body, fontWeight: FW.medium, cursor: 'pointer',
  },
  btnDanger: {
    backgroundColor: 'transparent', color: DANGER,
    border: `1px solid ${DANGER}`, borderRadius: R.md, padding: '8px 16px',
    fontSize: FS.body, fontWeight: FW.medium, cursor: 'pointer',
  },
  btnDisabled: { opacity: 0.55, cursor: 'not-allowed' },

  // Inputs
  input: {
    padding: '8px 12px',
    border: `1px solid ${dark ? BORDER_INPUT_DARK : BORDER_INPUT}`,
    borderRadius: R.md, fontSize: FS.body,
    backgroundColor: dark ? '#243058' : SURFACE,
    color: dark ? TEXT_DARK_MODE : TEXT_PRIMARY,
    outline: 'none', width: '100%', boxSizing: 'border-box',
  },

  // Tables
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '10px 12px',
    borderBottom: `0.5px solid ${dark ? BORDER_DARK : BORDER}`,
    ...LABEL_CAPS, color: dark ? TEXT_MUTED : TEXT_FAINT,
  },
  td: {
    padding: '10px 12px', fontSize: FS.body,
    color: dark ? TEXT_DARK_MODE : TEXT_PRIMARY,
    borderBottom: `0.5px solid ${dark ? 'rgba(201,168,76,0.08)' : '#f5f2ee'}`,
  },

  // Banners
  /** Read-failure banner. The audit's 6.2/6.4 fix: a failed GET must be visually distinct from
   *  a genuine empty state, never rendered with `empty` above. */
  loadError: {
    backgroundColor: dark ? 'rgba(231,76,60,0.12)' : '#fde8e8',
    border: `1px solid ${DANGER}`, color: dark ? '#ffb4ab' : '#a33',
    borderRadius: R.md, padding: `${SP.md} ${SP.lg}`, fontSize: FS.body,
    display: 'flex', alignItems: 'center', gap: SP.sm,
  },
  /** Single "needs attention" recipe, replacing the audit's 5 competing ambers (1.2). */
  warnBanner: {
    backgroundColor: dark ? 'rgba(243,156,18,0.12)' : WARNING_BG,
    border: `1px solid ${WARNING_BORDER}`,
    color: dark ? '#ffd08a' : WARNING_TEXT,
    borderRadius: R.md, padding: `${SP.md} ${SP.lg}`, fontSize: FS.body,
  },
  successBanner: {
    backgroundColor: dark ? 'rgba(39,174,96,0.12)' : '#eafaf1',
    border: `1px solid ${SUCCESS}`, color: dark ? '#8ee0ad' : '#1e7e45',
    borderRadius: R.md, padding: `${SP.md} ${SP.lg}`, fontSize: FS.body,
  },

  // Badges
  // A11YCONTRAST (audit A3a): white on GOLD measured 2.29:1, far below the 4.5:1 AA threshold
  // for normal text, and this badge is micro-sized so the large-text allowance does not apply.
  //
  // NAVY TEXT ON THE GOLD FILL, NOT GOLD_600 ON WHITE. The task offered either, "whichever
  // keeps the badge recognisable, at a contrast of 4.5:1 or more". GOLD_600 on white MEASURES
  // 3.43:1 and therefore does not satisfy the second half of that condition, so it was not an
  // available option. Navy on gold measures 7.46:1 with NAVY_FORM, and keeps the gold fill that
  // makes the badge recognisable at a glance, which is what the first half asks for. Both
  // numbers are computed, not estimated - see the A11YCONTRAST test.
  badgeGold:   { backgroundColor: GOLD, color: NAVY_FORM, borderRadius: R.full, padding: '1px 6px', fontSize: FS.micro, fontWeight: FW.semibold },
  badgeDanger: { backgroundColor: DANGER, color: '#fff', borderRadius: R.full, padding: '1px 6px', fontSize: FS.micro, fontWeight: FW.semibold },
  badgeNeutral:{ backgroundColor: dark ? '#243058' : '#f5f5f5', color: dark ? TEXT_MUTED : TEXT_FAINT, borderRadius: R.sm, padding: '2px 8px', fontSize: FS.micro },

  // Navigation
  // RESPONSIVE Phase 3: derived from the canonical sidebarChrome shape (AdminPanel's 200px sidebar).
  // Values (200px, 0.5px border, surface/border colours) are byte-identical to the previous inline def.
  sidebar: sidebarChrome({
    width: '200px',
    surface: dark ? SURFACE_DARK : SURFACE,
    border: `0.5px solid ${dark ? BORDER_DARK : BORDER}`,
  }),
  navItem: {
    display: 'flex', alignItems: 'center', gap: SP.sm, width: '100%',
    padding: '8px 16px', fontSize: FS.body,
    color: dark ? TEXT_MUTED : '#888',
    border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
    borderLeft: '2px solid transparent',
  },
  navItemActive: {
    display: 'flex', alignItems: 'center', gap: SP.sm, width: '100%',
    padding: '8px 16px', fontSize: FS.body,
    color: dark ? GOLD : '#8b6914',
    border: 'none', backgroundColor: dark ? 'rgba(201,168,76,0.1)' : '#fff8ec',
    cursor: 'pointer', textAlign: 'left',
    borderLeft: `2px solid ${GOLD}`, fontWeight: FW.medium,
  },
  /** Collapsible sidebar group header - the "shock absorber" for the COO's crowding concern
   *  (audit 3.1): future features go inside an existing group instead of lengthening the list. */
  navGroup: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    width: '100%', padding: `${SP.md} ${SP.lg} ${SP.xs}`,
    background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
    ...LABEL_CAPS, color: dark ? TEXT_MUTED : TEXT_FAINT,
  },

  main: { flex: 1, padding: `${SP.xl} ${SP.xxl}`, minWidth: 0 },
});

export default mkT;

// ═══════════════════════════════════════════════════════════════════════════
// REDESIGN Stage 1: the approved design system (ENKO_DESIGN_REVIEW.html)
// ═══════════════════════════════════════════════════════════════════════════
// Everything above is Stage 0 and is left untouched, so nothing that already imports it moves.
// This block is what Dashboard.js adopts in Stage 1; the remaining 4 shells adopt it in Stage 2+.

/** The one font stack, matching index.css. Inter is self-hosted (index.js). */
export const FONT_STACK =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

/** Six-step type scale. Weight carries the hierarchy, not size alone: the review found only 2px
 *  separated a card title from body text at similar weight, so nothing read as a heading.
 *  Negative tracking on the large steps is the detail most missing from the current app. */
export const TYPE = {
  display: { fontSize: '28px',   fontWeight: 660, letterSpacing: '-0.02em',  lineHeight: 1 },
  h2:      { fontSize: '19px',   fontWeight: 620, letterSpacing: '-0.011em', lineHeight: 1.3 },
  h3:      { fontSize: '15px',   fontWeight: 600, letterSpacing: '-0.006em', lineHeight: 1.35 },
  body:    { fontSize: '13.5px', fontWeight: 400, lineHeight: 1.5 },
  meta:    { fontSize: '12.5px', fontWeight: 400, lineHeight: 1.45 },
  label:   { fontSize: '10.5px', fontWeight: 660, letterSpacing: '0.06em',
             textTransform: 'uppercase' },
};

/** 4px spacing scale. */
export const S = { 1: '4px', 2: '8px', 3: '12px', 4: '16px', 5: '20px', 6: '24px', 8: '32px', 10: '40px' };

/** Four radii, replacing the 11 currently in use. */
export const RADIUS = { sm: '4px', md: '6px', lg: '8px', xl: '12px', pill: '999px' };

/** Three elevation tokens, replacing 18 shadow recipes. Layered rather than single-blur, which
 *  is what makes a shadow read as intentional. Dark mode drops to none: a shadow is invisible on
 *  a dark ground, so depth there comes from surface lightness instead. */
export const ELEV = {
  e1: '0 1px 2px rgba(16,24,40,.06)',
  e2: '0 4px 8px -2px rgba(16,24,40,.08), 0 2px 4px -2px rgba(16,24,40,.04)',
  e3: '0 12px 16px -4px rgba(16,24,40,.08), 0 4px 6px -2px rgba(16,24,40,.03)',
};

/** Semantic surface tokens, resolved per theme. */
export const tokens = (dark) => ({
  bg:        dark ? '#0b111f' : N_50,      // page ground: cards sit ON this, never on white
  surface:   dark ? '#131c2e' : N_0,
  line:      dark ? '#25324c' : N_200,
  lineSoft:  dark ? '#1b2437' : N_100,
  tx:        dark ? '#eaecf0' : N_900,
  tx2:       dark ? '#b6bdc9' : N_600,
  tx3:       dark ? '#8e98a8' : N_500,
  tx4:       dark ? '#697386' : N_400,
  hover:     dark ? '#1b2437' : N_100,
  e1:        dark ? 'none' : ELEV.e1,
  e2:        dark ? 'none' : ELEV.e2,
  okBg:      dark ? '#0b2a1c' : OK_50,     okFg:   OK_600,
  warnBg:    dark ? '#2b1f08' : WARN_50,   warnFg: WARN_600,
  critBg:    dark ? '#2c1310' : CRIT_50,   critFg: CRIT_600,
  crit:      CRIT_500,
  navy:      NAVY_700,
  navyHover: NAVY_600,
  gold:      GOLD_500,
  border:    dark ? '#2f3d5a' : N_300,     // stronger line, for inputs and outline buttons
});

/** Four button variants sharing ONE geometry, per the approved spec. The 1px border on every
 *  variant is deliberate: it gives all four an identical silhouette, so a destructive action can
 *  never look more inviting than the safe one sitting beside it. Hover/focus are returned as
 *  separate objects because inline styles cannot express pseudo-classes; Btn in ui.js applies
 *  them via handlers, the same pattern this codebase already uses elsewhere. */
export const BTN_BASE = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
  fontFamily: 'inherit', fontSize: '13.5px', fontWeight: 570, padding: '8px 14px',
  borderRadius: RADIUS.md, cursor: 'pointer', border: '1px solid transparent',
  lineHeight: 1.25, transition: 'background .12s, border-color .12s, box-shadow .12s',
  whiteSpace: 'nowrap',
};

export const btnVariant = (variant, dark) => {
  const t = tokens(dark);
  switch (variant) {
    case 'secondary':
      return { base: { ...BTN_BASE, background: t.surface, color: t.tx2, borderColor: t.border },
               hover: { background: t.hover, color: t.tx } };
    case 'danger':
      return { base: { ...BTN_BASE, background: t.surface, color: t.critFg, borderColor: '#fda29b' },
               hover: { background: t.critBg } };
    case 'ghost':
      return { base: { ...BTN_BASE, background: 'transparent', color: t.tx3,
                       borderColor: 'transparent', padding: '8px 10px' },
               hover: { background: t.hover, color: t.tx } };
    case 'primary':
    default:
      return { base: { ...BTN_BASE, background: t.navy, color: '#fff', boxShadow: t.e1 },
               hover: { background: t.navyHover } };
  }
};

export const BTN_FOCUS = { boxShadow: '0 0 0 3px rgba(26,39,68,.16)', outline: 'none' };
export const BTN_DISABLED = { opacity: 0.55, cursor: 'not-allowed' };

// ── REDESIGN Stage 2: shared shell chrome ───────────────────────────────────
// Stage 1 proved these values on Dashboard.js. Rather than copy them into each of the remaining
// shells (which is exactly how the app ended up with 5 divergent implementations in the first
// place), the sidebar/card/page pieces are defined ONCE here and consumed by every shell.
// Dashboard.js keeps its own mkS - it already resolves from the same tokens, so it renders
// identically; it is deliberately not refactored in this stage to keep its diff at zero.

/** Sidebar nav item + active state. The active state is the approved solid navy fill with white
 *  text; gold is reserved for the icon inside it (see navIconColor below). */
export const shellNav = (dark) => {
  const t = tokens(dark);
  const shared = {
    display: 'flex', alignItems: 'center', gap: '10px', width: 'calc(100% - 16px)',
    margin: '1px 8px', padding: '7px 10px', fontSize: '13.5px', border: 'none',
    cursor: 'pointer', textAlign: 'left', borderRadius: RADIUS.md,
  };
  return {
    item:       { ...shared, color: t.tx2, background: 'none' },
    itemActive: { ...shared, color: '#fff', backgroundColor: t.navy, fontWeight: 560 },
    sectionLabel: { ...TYPE.label, color: t.tx4, margin: `${S[4]} ${S[3]} ${S[1]} 18px` },
    // RESPONSIVE Phase 3: derived from the canonical sidebarChrome shape (the 240px shell sidebar,
    // consumed by EmployeeView). Values byte-identical to the previous inline def.
    sidebar: sidebarChrome({
      width: '240px', surface: t.surface, border: `1px solid ${t.line}`,
      extra: { minHeight: 'calc(100vh - 53px)', paddingTop: S[2] },
    }),
  };
};

/** Gold only ever colours the icon of the ACTIVE item - the one job it keeps after the review
 *  demoted it from brand + nav + badge + primary button down to a single accent. */
export const navIconColor = (isActive, dark) => (isActive ? GOLD_500 : tokens(dark).tx3);

// ── FORMBTNS: shared action-row button set (Back / Save draft / Submit) ──────
// FORMBTNSHARE (confirmed COO decision): every multi-section form's action row - Back to
// dashboard, Save draft, Submit - must be VISUALLY IDENTICAL across the whole app (same padding,
// font size, border-radius). The three forms (NewStarterForm, SponsorForm, LeaverForm) had
// matching saveBtn/submitBtn (12px 24px, 15px, radius 8, weight 500) but each reused a page-header
// "backBtn" in the row (9px 18px, 14px, radius 6, a stray marginBottom:24px) which sat smaller and
// misaligned. These three constants are the ONE definition each form now spreads into its own
// local styles, so the geometry can never drift again. Values are the existing saveBtn/submitBtn
// geometry, unchanged, so save/submit look bit-identical to before and only Back converges to
// match. This is scoped strictly to the action-row trio - page-header back links, modal
// cancel/submit buttons, and every other button keep their own styles.
export const FORM_BACK_BTN = {
  background: 'none', border: `1.5px solid rgba(26,39,68,0.25)`, color: NAVY_FORM,
  borderRadius: '8px', padding: '12px 24px', fontSize: '15px',
  fontWeight: '500', cursor: 'pointer',
};
export const FORM_SAVE_BTN = {
  backgroundColor: '#fff', color: NAVY_FORM, border: `1.5px solid ${NAVY_FORM}`,
  borderRadius: '8px', padding: '12px 24px', fontSize: '15px',
  fontWeight: '500', cursor: 'pointer',
};
export const FORM_SUBMIT_BTN = {
  // Transparent border (not 'none') so Submit's outer box is byte-identical to its bordered
  // Back/Save siblings - border:none left it 1.5px shorter/narrower per side in the same row.
  // Transparent over the button's own navy fill renders exactly like no border, only sized right.
  backgroundColor: NAVY_FORM, color: '#fff', border: '1.5px solid transparent',
  borderRadius: '8px', padding: '12px 24px', fontSize: '15px',
  fontWeight: '500', cursor: 'pointer',
};

/** Page ground and card, matching Dashboard exactly. Elevation comes from the ground/card
 *  contrast, not from a heavy shadow. */
export const shellSurface = (dark) => {
  const t = tokens(dark);
  return {
    page: { backgroundColor: t.bg, minHeight: '100vh', fontFamily: FONT_STACK },
    main: { flex: 1, padding: `${S[5]} ${S[6]}`, minWidth: 0, backgroundColor: t.bg },
    card: {
      backgroundColor: t.surface, borderRadius: RADIUS.xl, padding: S[5],
      marginBottom: S[4], boxShadow: t.e1, border: `1px solid ${t.line}`,
    },
    cardTitle: { ...TYPE.h3, color: t.tx, margin: `0 0 ${S[3]} 0` },
    empty: { ...TYPE.meta, color: t.tx3, margin: `${S[2]} 0 0 0` },
  };
};
